<template>
  <div class="container">
    <div class="white-content">
      <div class="header1">
        <router-link to="/" style="text-decoration: none; color: black;">
          <h2>&lt;X-LIGHT</h2>
        </router-link>
      </div>
      <div class="function-container">
        <div class="showProgress" v-if="showProgress">
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: progress.toFixed(2) + '%' }"></div>
          </div>
          <p>{{ progress.toFixed(2) }}%</p>
        </div>
        <div class="function-item TJ" :class="{ selected: isTJSelected }" @click="showPls">
          <p>灯珠数量</p>
        </div>
      </div>
    </div>

    <div class="gray-bg">
      <div class="content">
        <div class="settings-grid">
          <div class="setting-item" v-for="(item, index) in settings" :key="index"
            :class="{ active: selectedSetting === index && !isShowPls }" @click="selectSetting(index)">
            <img :src="item.icon" :alt="item.name" />
            <span>{{ item.name }}</span>
          </div>
        </div>

        <div class="control-panel">
          <div v-if="isShowPls">
            <div class="title-and-button-container">
              <h3 class="setting-title">灯珠数量设置</h3>
              <button class="apply-button" @click="applyLengthSettings">确认</button>
            </div>
            <div class="slider-container">
              <label>灯珠数量: {{ lightStripLength }} 颗</label>
              <!-- 使用 .number 修饰符确保为数字类型 -->
              <input type="range" min="240" max="500" v-model.number="lightStripLength"
                :style="{ background: lightStripLengthSliderBackground }" />
            </div>
          </div>

          <div v-else-if="selectedSetting !== null">
            <div class="title-and-button-container">
              <h3 class="setting-title">{{ settings[selectedSetting].name }}</h3>
              <button class="apply-button" @click="applySettings">确认</button>
            </div>

            <div class="color-options" v-if="[0, 1, 2, 3].includes(settings[selectedSetting].mode)">
              <label>颜色:</label>
              <div class="color-grid">
                <div v-for="(color, name) in predefinedColors" :key="name" class="color-swatch"
                  :style="{ backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})` }"
                  :class="{ 'selected-color': selectedColor === name }" @click="selectColor(name)">
                </div>
              </div>
            </div>

            <!-- 子模式现在应用于拾音模式 (mode: 3) -->
            <div class="light-mode-selector" v-if="settings[selectedSetting].mode === 3">
              <label>子模式:</label>
              <div class="checkbox-row">
                <label><input type="radio" :value="0" v-model="subMode"> 单色闪烁</label>
                <label><input type="radio" :value="1" v-model="subMode"> 七色变化</label>
                <label><input type="radio" :value="2" v-model="subMode"> 彩虹闪烁</label>
              </div>
            </div>

            <div class="slider-container">
              <label>亮度: {{ brightness }}%</label>
              <!-- 使用 .number 修饰符确保为数字类型 -->
              <input type="range" min="0" max="100" v-model.number="brightness"
                :style="{ background: brightnessSliderBackground }" />
            </div>

            <div class="slider-container" v-if="[1, 2, 5].includes(settings[selectedSetting].mode)">
              <label>速度: {{ speed }}%</label>
              <!-- 使用 .number 修饰符确保为数字类型 -->
              <input type="range" min="0" max="100" v-model.number="speed" :style="{ background: speedSliderBackground }" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { NUS_SERVICE_UUID, NUS_RX_CHAR_UUID } from '@/components/nordicUARTServiceUUIDs';

export default {
  name: 'LightControl',
  data() {
    return {
      isTJSelected: false,
      showProgress: false,
      batteryPercentage: 50,
      status: '固件更新',
      progress: 0,

      // 更新后的模式映射
      settings: [
        { name: '常亮', icon: require('@/assets/V1.png'), mode: 0 },
        { name: '呼吸', icon: require('@/assets/V2.png'), mode: 1 },
        { name: '闪烁', icon: require('@/assets/V3.png'), mode: 2 },
        { name: '拾音', icon: require('@/assets/V4.png'), mode: 3 }, // 拾音带子模式
        { name: '彩色循环', icon: require('@/assets/V5.png'), mode: 4 },
        { name: '彩虹', icon: require('@/assets/V6.png'), mode: 5 },
      ],
      selectedSetting: 0,
      brightness: 50,
      speed: 10,
      subMode: 0,
      isShowPls: false,
      lightStripLength: 240,

      predefinedColors: {
        '白': { r: 255, g: 255, b: 255 },
        '红': { r: 255, g: 0, b: 0 },
        '橙': { r: 255, g: 165, b: 0 },
        '黄': { r: 255, g: 255, b: 0 },
        '绿': { r: 0, g: 128, b: 0 },
        '青': { r: 0, g: 255, b: 255 },
        '蓝': { r: 0, g: 0, b: 255 },
        '紫': { r: 128, g: 0, b: 128 },
      },
      selectedColor: '白',

      rxCharacteristic: null,
    };
  },
  computed: {
    ...mapGetters([
      'getIsBleConnected',
      'getGattServer',
    ]),
    brightnessSliderBackground() {
      return `linear-gradient(to right, #00c853 0%, #00c853 ${this.brightness}%, #e0e0e0 ${this.brightness}%, #e0e0e0 100%)`;
    },
    speedSliderBackground() {
      return `linear-gradient(to right, #00c853 0%, #00c853 ${this.speed}%, #e0e0e0 ${this.speed}%, #e0e0e0 100%)`;
    },
    lightStripLengthSliderBackground() {
      const min = 240;
      const max = 500;
      const percentage = ((this.lightStripLength - min) / (max - min)) * 100;
      return `linear-gradient(to right, #00c853 0%, #00c853 ${percentage}%, #e0e0e0 ${percentage}%, #e0e0e0 100%)`;
    }
  },
  watch: {
    getIsBleConnected(newVal) {
      if (newVal) {
        this.initializeBleCharacteristics();
      } else {
        this.rxCharacteristic = null;
        // 使用自定义模态框代替alert
        this.showCustomAlert('蓝牙连接已断开，请返回首页重新连接。');
        this.$router.push('/');
      }
    },
    getGattServer(newServer) {
        if (newServer && newServer.connected) {
            this.initializeBleCharacteristics();
        } else {
            this.rxCharacteristic = null;
        }
    }
  },
  async mounted() {
    if (!this.getIsBleConnected || !this.getGattServer) {
      // 使用自定义模态框代替alert
      this.showCustomAlert('蓝牙未连接，请先连接蓝牙设备！');
      this.$router.push('/');
    } else {
      await this.initializeBleCharacteristics();
    }
  },
  methods: {
    showCustomAlert(message) {
      // 可以在这里实现一个自定义的模态框，而不是使用 alert()
      // 由于没有提供模态框组件，这里只做简单的 console.log
      console.warn(`Custom Alert: ${message}`);
      // 实际应用中，你可能需要一个 v-if 控制的 div 来显示信息
    },
    clearInputs() {
      this.brightness = 50;
      this.speed = 10;
      this.selectedColor = '白';
      this.subMode = 0; // 重置为默认值
    },
    selectSetting(index) {
      this.selectedSetting = index;
      this.isShowPls = false;
      this.clearInputs();
    },
    showPls() {
      this.isShowPls = true;
      this.selectedSetting = null;
    },
    selectColor(colorName) {
      this.selectedColor = colorName;
    },

    async initializeBleCharacteristics() {
      try {
        if (!this.getGattServer || !this.getGattServer.connected) {
          console.warn('GATT Server not connected, cannot initialize characteristics.');
          return;
        }
        const service = await this.getGattServer.getPrimaryService(NUS_SERVICE_UUID);
        this.rxCharacteristic = await service.getCharacteristic(NUS_RX_CHAR_UUID);
        console.log('NUS RX Characteristic initialized:', this.rxCharacteristic);
      } catch (error) {
        console.error('初始化蓝牙特性失败:', error);
        this.showCustomAlert('初始化蓝牙特性失败，请尝试重新连接设备。');
        this.$router.push('/');
      }
    },

    async sendBleData(data) {
      if (!this.getIsBleConnected || !this.rxCharacteristic) {
        this.showCustomAlert('蓝牙未连接或特性未准备好，无法发送数据。');
        console.error('蓝牙未连接或 RX Characteristic 未准备好。');
        return;
      }
      try {
        const jsonString = JSON.stringify(data) + '\n';
        const encoder = new TextEncoder();
        const encodedData = encoder.encode(jsonString);
        await this.rxCharacteristic.writeValueWithoutResponse(encodedData);
        console.log('蓝牙数据发送成功:', jsonString);
      } catch (error) {
        console.error('发送蓝牙数据失败:', error);
        this.showCustomAlert(`发送蓝牙数据失败: ${error.message}`);
      }
    },

    async applySettings() {
      const selectedSetting = this.settings[this.selectedSetting];
      const selectedMode = selectedSetting.mode;
      const convertedBrightness = Math.round((this.brightness / 100) * 255);

      let payload = {
        "type": "SetLed",
        "numLEDs": this.lightStripLength,
        "mode": selectedMode,
        "brightness": convertedBrightness,
      };

      // 常亮(0)、呼吸(1)、闪烁(2)、拾音(3)模式需要颜色
      if ([0, 1, 2, 3].includes(selectedMode)) {
        const rgb = this.predefinedColors[this.selectedColor];
        if (rgb) {
          payload.r = rgb.r;
          payload.g = rgb.g;
          payload.b = rgb.b;
        }
      }

      // 呼吸(1)、闪烁(2)、彩虹(5)模式需要速度
      if ([1, 2, 5].includes(selectedMode)) {
        payload.speed = this.speed;
      }
      
      // 拾音模式 (mode: 3) 带子模式
      if (selectedMode === 3) {
        payload.subMode = this.subMode;
      }
      
      console.log('准备发送的蓝牙数据:', payload);
      await this.sendBleData(payload);
    },

    async applyLengthSettings() {
      const payload = {
        "type": "SetLed",
        "numLEDs": this.lightStripLength,
      };
      console.log('准备发送的蓝牙长度数据:', payload);
      await this.sendBleData(payload);
      this.showCustomAlert(`灯珠数量设置已应用：\n数量：${this.lightStripLength} 颗`);
    },
  }
};
</script>

<style scoped>
/* 容器和通用布局 */
.container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f0f2f5;
}

.white-content {
  flex: 0 0 200px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
  padding-top: 20px;
}

.header1 {
  width: 100%;
  text-align: center;
  padding-bottom: 30px;
  border-bottom: 1px solid #eee;
  margin-bottom: 30px;
}

.header1 h2 {
  font-size: 24px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.function-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 80%;
}

.function-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
  justify-content: center;
  background-color: #f9f9f9;
}

.function-item:hover {
  background-color: #e6f7ff;
  border-color: #91d5ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 150, 255, 0.1);
}

.function-item.selected {
  background-color: #00c853;
  border-color: #00c853;
  color: #fff;
  box-shadow: 0 4px 8px rgba(0, 200, 83, 0.3);
}

.function-item p {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

/* 右侧控制区 */
.gray-bg {
  flex: 1;
    background-color: #f0f2f5;
    padding: 30px;
    display: flex
;
    justify-content: center;
    align-items: center;
    flex-direction: column;
}

.content {
  display: flex;
  gap: 50px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  flex: 0 0 auto;
  padding: 20px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
  margin-top: 50px;
  align-self: flex-start;
  /* 增加整个网格容器的宽度 */
  width: 450px;
  padding: 30px;
}

.setting-item {
  width: 130px;
  height: 130px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.2s ease-in-out;
}

.setting-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 12px rgba(0, 0, 0, 0.1);
  border-color: #00c853;
}

.setting-item img {
  max-width: 60px;
  margin-bottom: 8px;
}

.setting-item span {
  font-size: 16px; /* 放大字体 */
  font-weight: 500;
  color: #555;
}

.setting-item.active {
  background-color: #00c853;
  color: #fff;
  border-color: #00c853;
  box-shadow: 0 5px 12px rgba(0, 200, 83, 0.3);
}

.setting-item.active span {
  color: #fff;
}

/* 固定控制面板的尺寸和位置 */
.control-panel {
  min-width: 400px; /* 固定最小宽度 */
  min-height: 450px; /* 固定最小高度，确保不跳动 */
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.08);
  padding: 25px;
  display: flex;
  flex-direction: column;
  margin-top: 50px;
  align-self: flex-start;
  transition: min-width 0.3s ease, min-height 0.3s ease; /* 添加过渡效果 */
}

/* 确保内部内容在隐藏时不会改变面板尺寸 */
.control-panel > div {
  flex-grow: 1; /* 确保内容块填充可用空间 */
  display: flex;
  flex-direction: column;
}

.title-and-button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.setting-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.apply-button {
  padding: 10px 20px;
  background-color: #00c853;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out;
}

.apply-button:hover {
  background-color: #009624;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 150, 36, 0.2);
}

.slider-container {
  width: 100%;
  margin-bottom: 30px;
}

.slider-container label {
  display: block;
  margin-bottom: 10px;
  font-size: 16px;
  color: #555;
  font-weight: 500;
}

/* 美化滑块 */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 5px;
  outline: none;
  transition: background 0.2s ease-in-out;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #00c853;
  cursor: grab;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  margin-top: -6px;
  transition: background 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="range"]::-webkit-slider-thumb:active {
  cursor: grabbing;
  box-shadow: 0 0 8px rgba(0, 200, 83, 0.5);
}

input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #00c853;
  cursor: grab;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  border: none;
  transition: background 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="range"]::-moz-range-thumb:active {
  cursor: grabbing;
  box-shadow: 0 0 8px rgba(0, 200, 83, 0.5);
}

input[type="range"]::-ms-thumb {
  width: 20px;
  height: 20px;
  background: #00c853;
  cursor: grab;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  margin-top: -6px;
  transition: background 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

input[type="range"]::-ms-thumb:active {
  cursor: grabbing;
  box-shadow: 0 0 8px rgba(0, 200, 83, 0.5);
}

.color-options {
  margin-bottom: 30px;
}

.color-options label {
  display: block;
  margin-bottom: 10px;
  font-size: 16px;
  color: #555;
  font-weight: 500;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 10px;
}

.color-swatch {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.2s ease-in-out, border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.color-swatch:hover {
  transform: scale(1.1);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.color-swatch.selected-color {
  border-color: #00c853;
  box-shadow: 0 0 12px rgba(0, 200, 83, 0.6);
  transform: scale(1.05);
}

.light-mode-selector {
  margin-bottom: 30px;
}

.light-mode-selector > label {
  font-size: 16px;
  color: #555;
  font-weight: 500;
  margin-bottom: 10px;
  display: block;
}

.checkbox-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkbox-row label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 15px;
  color: #444;
  margin-bottom: 0;
}

.checkbox-row input[type="radio"] {
  margin-right: 8px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #ccc;
  border-radius: 50%;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  top: 0;
}

.checkbox-row input[type="radio"]:checked {
  border-color: #00c853;
  background-color: #00c853;
}

.checkbox-row input[type="radio"]:checked::before {
  content: '';
  display: block;
  width: 8px;
  height: 8px;
  background-color: #fff;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.checkbox-row label:hover input[type="radio"] {
  border-color: #00c853;
}
</style>
